import moment from 'moment';
import { parseQueryNotes } from 'src/utils/queryNotesUtils';
import api from 'src/api/api';

// Common fetcher function for API calls
export const fetcher = async (url, data = {}) => {
  const formData = new FormData();
  Object.keys(data).forEach((key) => formData.append(key, data[key]));

  const res = await api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return res.data.data;
};

export const getPitchForExpert = (query, expertId) => {
  const lead = query.leads.find((l) => l.expertID === expertId);
  return lead?.currentSendToExpert || lead?.initialSendToExpert || '';
};

// Get the date to use for a query based on priority order
// Priority: reportDate > sentToReporterDate > dueDate > sentToExpertDate > pullTime > updateTime
export const getQueryDateForDisplay = (query) => {
  const parsedNotes = parseQueryNotes(query.notes);

  if (parsedNotes.reportDate) {
    return parsedNotes.reportDate;
  } else if (query.dueDate) {
    return query.dueDate;
  } else if (query.sentToExpertDate) {
    return query.sentToExpertDate;
  } else if (query.pullTime) {
    return query.pullTime;
  } else {
    return query.updateTime;
  }
};

// Filter the queries by date range
export const filterByDateRange = (queries, selectedRange) => {
  return queries.filter((query) => {
    const dateToUse = moment(getQueryDateForDisplay(query));
    return dateToUse.isBetween(selectedRange.start, selectedRange.end, 'day', '[]');
  });
};

// Create a lookup map for experts by ID (O(1) lookups instead of O(n))
export const createExpertMap = (experts) => {
  if (!experts) return new Map();
  return new Map(experts.map((e) => [e.id, e]));
};

// Find expert for a query (handles both legacy expertID and new expertsID)
// If expertMap is provided, uses O(1) lookup; otherwise falls back to O(n) find
export const findExpertForQuery = (query, experts, expertMap = null) => {
  if (expertMap) {
    // O(1) lookup using map
    const expertByNewId = expertMap.get(Number(query.expertsID));
    const expertByLegacyId = expertMap.get(query.expertID);
    return expertByNewId || expertByLegacyId;
  }
  // Fallback to O(n) find for backward compatibility
  const expertByLegacyId = experts.find((e) => e.id === query.expertID);
  const expertByNewId = experts.find((e) => e.id === Number(query.expertsID));
  return expertByNewId || expertByLegacyId;
};

// Group queries by company and experts
export const groupQueriesByCompany = (queries, experts, companiesWithActiveExperts, expertMap = null) => {
  // Create map if not provided (for backward compatibility)
  const map = expertMap || createExpertMap(experts);

  const groupedQueries = queries.reduce((acc, query) => {
    const expert = findExpertForQuery(query, experts, map);

    if (!expert) {
      // Handle unassigned queries
      const unassignedCompany = 'Unassigned Queries';
      if (!acc[unassignedCompany]) {
        acc[unassignedCompany] = {
          company: unassignedCompany,
          experts: [],
          queriesByExpert: {},
          isUnassigned: true,
        };
      }

      if (!acc[unassignedCompany].queriesByExpert['unassigned']) {
        acc[unassignedCompany].queriesByExpert['unassigned'] = {
          expert: { id: 'unassigned', name: 'Unassigned', company: 'Unassigned Queries', isActive: 1 },
          queries: [],
        };
      }

      acc[unassignedCompany].queriesByExpert['unassigned'].queries.push(query);
      return acc;
    }

    const company = expert.company || 'Unknown Company';
    if (!acc[company]) {
      acc[company] = {
        company,
        experts: [],
        queriesByExpert: {},
      };
    }

    if (!acc[company].queriesByExpert[expert.id]) {
      acc[company].queriesByExpert[expert.id] = {
        expert,
        queries: [],
      };
    }

    acc[company].queriesByExpert[expert.id].queries.push(query);
    return acc;
  }, {});

  // Add companies with active experts that don't have queries in the date range
  if (companiesWithActiveExperts) {
    Object.entries(companiesWithActiveExperts).forEach(([company, data]) => {
      if (data.hasActiveExperts && !groupedQueries[company]) {
        groupedQueries[company] = {
          company,
          experts: data.experts,
          queriesByExpert: {},
        };
      }
    });
  }

  return groupedQueries;
};

// Filter companies by search query
export const filterCompaniesBySearch = (queriesByCompany, searchQuery, companiesWithActiveExperts) => {
  if (!searchQuery.trim()) return queriesByCompany;

  const searchTerm = searchQuery.toLowerCase().trim();
  const filteredCompanies = {};

  Object.entries(queriesByCompany).forEach(([company, data]) => {
    // Handle unassigned queries separately
    if (data.isUnassigned) {
      const queryMatches = Object.values(data.queriesByExpert).some(({ queries }) =>
        queries.some((query) => query.title.toLowerCase().includes(searchTerm) || query.mediaOutlet.toLowerCase().includes(searchTerm))
      );

      if (company.toLowerCase().includes(searchTerm) || queryMatches) {
        filteredCompanies[company] = data;
      }
      return;
    }

    const companyData = companiesWithActiveExperts?.[company];
    if (!companyData?.hasActiveExperts) return;

    const companyMatches = company.toLowerCase().includes(searchTerm);
    const expertMatches = Object.values(data.queriesByExpert).some(({ expert }) => expert.name.toLowerCase().includes(searchTerm));
    const activeExpertMatches = companyData.experts.some((expert) => expert.isActive === 1 && expert.name.toLowerCase().includes(searchTerm));

    if (companyMatches || expertMatches || activeExpertMatches) {
      filteredCompanies[company] = {
        ...data,
        queriesByExpert: Object.fromEntries(
          Object.entries(data.queriesByExpert).filter(([, expertData]) => {
            if (expertData.expert.isActive !== 1) return false;
            return companyMatches || expertData.expert.name.toLowerCase().includes(searchTerm);
          })
        ),
      };
    }
  });

  return filteredCompanies;
};
